import { PictureAsPdfSharp } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Modal,
  Snackbar,
  SnackbarContent,
  Typography,
  IconButton,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import {
  createClientWithEquipment,
  getAllCustomersAndEquipments,
  updateCustomer,
} from "src/services/customers";
import * as Yup from "yup";
import AccordionComponent from "../AccordionComponent";
import {
  ButtonContainer,
  ButtonPdfContainerStyled,
  ButtonStyled,
  ClientContainter,
  EquipmentContainter,
  EquipmentListContainter,
  InputContainer,
  InputEquipmentContainer,
  ModalContainer,
} from "./styles";
import { deleteEquipment } from "src/services/equipments";
import { StyledLoadingContainer } from "../AccordionComponent/styles";
import StyledTextField from "../StyledTextField";
import { formatDocument, formatPhone } from "src/utils/formatters";
import LockOpenIcon from "@mui/icons-material/LockOpen";

export default function NewClientModal({
  open,
  handleClose,
  customer,
}: {
  open: boolean;
  handleClose: () => void;
  customer?: any;
}) {
  const [currentCustomer, setCurrentCustomer] = useState(customer || null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (customer) setCurrentCustomer(customer);
  }, [customer]);

  const validationSchema = Yup.object({
    name: Yup.string().min(3, "Mínimo de 3 caracteres").required("Campo obrigatório"),
    document: Yup.string().required("Campo obrigatório"),
    email: Yup.string().email("E-mail inválido").required("Campo obrigatório"),
    contactName: Yup.string().required("Campo obrigatório"),
    phone: Yup.string().required("Campo obrigatório"),
    equipmentName: Yup.string().min(3, "Mínimo de 3 caracteres").required("Campo obrigatório"),
    model: Yup.string().min(3, "Mínimo de 3 caracteres").required("Campo obrigatório"),
    technician: Yup.string().required("Campo obrigatório"),
    serialNumber: Yup.string().required("Campo obrigatório"),
    brand: Yup.string().min(3, "Mínimo de 3 caracteres").required("Campo obrigatório"),
    calibrationDate: Yup.date().required("Campo obrigatório").nullable(),
    expiration_date: Yup.date()
      .required("Campo obrigatório")
      .nullable()
      .test("is-after-calibration", "O vencimento deve ser posterior à data de calibração", function (value) {
        const { calibrationDate } = this.parent;
        if (!value || !calibrationDate) return true;
        const calib = new Date(calibrationDate);
        const venc = new Date(value);
        return venc >= calib;
      }),
    pdf_file: Yup.mixed<File>()
      .required("PDF é obrigatório")
      .test("fileFormat", "Apenas arquivos PDF são permitidos", (value) =>
        value ? value.type === "application/pdf" : false
      ),
  });

  const initialValues = {
    name: currentCustomer?.name || "",
    document: currentCustomer?.document || "",
    email: currentCustomer?.email || "",
    contactName: currentCustomer?.contact_name || "",
    phone: currentCustomer?.phone || "",
    equipmentName: "",
    model: "",
    technician: "",
    serialNumber: "",
    brand: "",
    calibrationDate: "",
    expiration_date: "",
    pdf_file: null as File | null,
  };

  const handleUnlock = () => {
    const now = new Date();
    const day = now.getDate();
    const month = now.getMonth() + 1;
    const minute = now.getMinutes();

    const minuteStr = String(minute).padStart(2, "0");
    const key = `${minuteStr}${day * month}`;

    const input = prompt("🔒 Digite a chave de acesso para desbloquear os campos:");
    if (input === key) {
      setUnlocked(true);
    } else {
      setToast({ message: "Chave incorreta ❌", type: "error" });
    }
  };

  const handleSave = async (values: typeof initialValues, { resetForm }: any) => {
    try {
      setLoading(true);

      const clientData = {
        name: values.name,
        document: values.document,
        contact_name: values.contactName,
        phone: values.phone,
        email: values.email,
        equipment: [
          {
            name: values.equipmentName,
            model: values.model,
            serial_number: values.serialNumber,
            brand: values.brand,
            technician: values.technician,
            calibration_date: values.calibrationDate,
            expiration_date: values.expiration_date,
            pdf_file: values.pdf_file instanceof File ? values.pdf_file : undefined,
          },
        ],
      };

      let response;

      if (currentCustomer?.id) {
        response = await updateCustomer(currentCustomer.id, clientData);
        setToast({ message: "Cliente atualizado com sucesso! ✅", type: "success" });
        setCurrentCustomer(response?.data);
      } else {
        response = await createClientWithEquipment(clientData);
        setToast({ message: "Cliente criado com sucesso! ✅", type: "success" });
        setCurrentCustomer(response?.data);
      }

      resetForm({
        values: {
          ...initialValues,
          name: response?.name || clientData.name,
          document: response?.document || clientData.document,
          email: response?.email || clientData.email,
          contactName: response?.contact_name || clientData.contact_name,
          phone: response?.phone || clientData.phone,
        },
      });
    } catch (error: any) {
      const { message } = error.response?.data || {};
      if (error.status === 422) setToast({ message, type: "error" });
      else setToast({ message: "Erro ao salvar dados do cliente ❌", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEquipment = async (id: number) => {
    if (!id) return;

    const confirmDelete = window.confirm("Tem certeza que deseja excluir este equipamento?");
    if (!confirmDelete) return;

    try {
      setDeleting(true);
      const response = await deleteEquipment(id);
      if (response?.status === 204) {
        setToast({ message: "Equipamento excluído com sucesso! ✅", type: "success" });

        setCurrentCustomer((prev: any) => ({
          ...prev,
          equipment: prev?.equipment?.filter((eq: any) => eq.id !== id) || [],
        }));
      } else {
        setToast({ message: "Erro ao excluir equipamento ❌.", type: "error" });
      }
    } catch {
      setToast({ message: "Erro ao excluir equipamento ❌.", type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalContainer>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSave}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              handleSubmit,
              resetForm,
            }) => (
              <form onSubmit={handleSubmit}>
                <ClientContainter>
                  <Typography
                    component="h2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    Dados do Cliente
                    <IconButton
                      size="small"
                      onClick={handleUnlock}
                      sx={{
                        opacity: 0,
                        width: 22,
                        height: 22,
                        ml: 1,
                        "&:hover": { opacity: 0.1, border: '1px solid #000' },
                      }}
                    >
                      <LockOpenIcon fontSize="small" />
                    </IconButton>
                  </Typography>

                  <InputContainer>
                    <StyledTextField
                      label="CNPJ/CPF"
                      name="document"
                      size="small"
                      margin="normal"
                      variant="outlined"
                      value={values.document}
                      onChange={(e) => {
                        const masked = formatDocument(e.target.value);
                        setFieldValue("document", masked);
                        if (currentCustomer) setCurrentCustomer(null);
                      }}
                      onBlur={async (e) => {
                        handleBlur(e);
                        const doc = e.target.value.replace(/\D/g, "");
                        if (!doc) return;

                        try {
                          setLoading(true);
                          const response = await getAllCustomersAndEquipments();
                          const customers = response?.data?.data || [];
                          const found = customers.find(
                            (c: any) => c.document.replace(/\D/g, "") === doc
                          );
                          if (found) {
                            setCurrentCustomer(found);
                            setFieldValue("name", found.name);
                            setFieldValue("contactName", found.contact_name);
                            setFieldValue("email", found.email);
                            setFieldValue("phone", formatPhone(found.phone));
                            setToast({
                              message: "Cliente encontrado e carregado! ✅",
                              type: "success",
                            });
                          }
                        } finally {
                          setLoading(false);
                        }
                      }}
                      error={touched.document && Boolean(errors.document)}
                      helperText={touched.document && (errors.document as string)}
                      fullWidth
                      disabled={!!currentCustomer?.id && !unlocked}
                    />

                    <StyledTextField
                      label="Cliente"
                      name="name"
                      size="small"
                      margin="normal"
                      variant="outlined"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && (errors.name as string)}
                      fullWidth
                      disabled={!!currentCustomer?.id && !unlocked}
                    />
                  </InputContainer>

                  <InputContainer>
                    <StyledTextField
                      label="Contato"
                      name="contactName"
                      size="small"
                      margin="normal"
                      variant="outlined"
                      value={values.contactName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.contactName && Boolean(errors.contactName)}
                      helperText={touched.contactName && (errors.contactName as string)}
                    />
                    <StyledTextField
                      label="Telefone"
                      name="phone"
                      size="small"
                      margin="normal"
                      variant="outlined"
                      value={values.phone}
                      onChange={(e) => {
                        const masked = formatPhone(e.target.value);
                        setFieldValue("phone", masked);
                      }}
                      onBlur={handleBlur}
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && (errors.phone as string)}
                    />
                    <StyledTextField
                      label="Email"
                      name="email"
                      type="email"
                      size="small"
                      margin="normal"
                      variant="outlined"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && (errors.email as string)}
                    />
                  </InputContainer>
                </ClientContainter>

                <EquipmentContainter>
                  <Typography component="h2" gutterBottom>
                    Novo Equipamento
                  </Typography>
                  <InputEquipmentContainer>
                    <StyledTextField
                      label="Equipamento"
                      name="equipmentName"
                      variant="outlined"
                      size="small"
                      value={values.equipmentName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.equipmentName && Boolean(errors.equipmentName)}
                      helperText={touched.equipmentName && (errors.equipmentName as string)}
                    />
                    <StyledTextField
                      label="Modelo"
                      name="model"
                      variant="outlined"
                      size="small"
                      value={values.model}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.model && Boolean(errors.model)}
                      helperText={touched.model && (errors.model as string)}
                    />
                    <StyledTextField
                      label="Nº série"
                      name="serialNumber"
                      variant="outlined"
                      size="small"
                      value={values.serialNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.serialNumber && Boolean(errors.serialNumber)}
                      helperText={touched.serialNumber && (errors.serialNumber as string)}
                    />
                    <StyledTextField
                      label="Marca"
                      name="brand"
                      variant="outlined"
                      size="small"
                      value={values.brand}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.brand && Boolean(errors.brand)}
                      helperText={touched.brand && (errors.brand as string)}
                    />
                  </InputEquipmentContainer>

                  <InputEquipmentContainer>
                    <StyledTextField
                      label="Técnico"
                      name="technician"
                      variant="outlined"
                      size="small"
                      value={values.technician}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.technician && Boolean(errors.technician)}
                      helperText={touched.technician && (errors.technician as string)}
                    />
                    <StyledTextField
                      label="Dt. Calibração"
                      name="calibrationDate"
                      type="date"
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={values.calibrationDate || ""}
                      onChange={(e) => setFieldValue("calibrationDate", e.target.value)}
                      error={touched.calibrationDate && Boolean(errors.calibrationDate)}
                      helperText={touched.calibrationDate && (errors.calibrationDate as string)}
                    />
                    <StyledTextField
                      label="Dt. Vencimento"
                      name="expiration_date"
                      type="date"
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={values.expiration_date || ""}
                      onChange={(e) => setFieldValue("expiration_date", e.target.value)}
                      error={touched.expiration_date && Boolean(errors.expiration_date)}
                      helperText={touched.expiration_date && (errors.expiration_date as string)}
                      sx={{
                        "& .MuiFormHelperText-root": {
                          whiteSpace: "normal",
                          lineHeight: 1,
                          fontSize: "11px",
                          marginBottom: '-2px'
                        },
                      }}
                    />

                    <input
                      type="file"
                      accept="application/pdf"
                      id="pdf-upload"
                      style={{ display: "none" }}
                      onChange={(event) => {
                        const file = event.currentTarget.files?.[0];
                        setFieldValue("pdf_file", file || null);
                      }}
                    />

                    <ButtonPdfContainerStyled>
                      <ButtonStyled
                        variant="contained"
                        startIcon={<PictureAsPdfSharp />}
                        type="button"
                        onClick={() => document.getElementById("pdf-upload")?.click()}
                      >
                        {values.pdf_file ? values.pdf_file.name : "Anexar PDF"}
                      </ButtonStyled>

                      <Typography variant="caption" className="error-text">
                        {touched.pdf_file && errors.pdf_file ? (errors.pdf_file as string) : ""}
                      </Typography>
                    </ButtonPdfContainerStyled>

                  </InputEquipmentContainer>

                  <EquipmentListContainter>
                    {currentCustomer?.equipment?.length > 0 && (
                      <AccordionComponent
                        equipments={currentCustomer.equipment}
                        onDelete={handleDeleteEquipment}
                      />
                    )}
                  </EquipmentListContainter>
                </EquipmentContainter>

                <ButtonContainer>
                  <Button
                    variant="outlined"
                    type="button"
                    onClick={() => {
                      setCurrentCustomer(null);
                      resetForm();
                      handleClose();
                    }}
                    disabled={loading}
                  >
                    Fechar
                  </Button>
                  <Button variant="contained" type="submit" disabled={loading}>
                    {loading ? <CircularProgress size={22} color="inherit" /> : "Salvar"}
                  </Button>
                </ButtonContainer>
              </form>
            )}
          </Formik>
        </ModalContainer>
      </Modal>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          message={toast?.message}
          sx={{
            backgroundColor: "#fff",
            color: toast?.type === "success" ? "#4caf50" : "#f44336",
            fontWeight: 700,
            borderRadius: 1,
          }}
        />
      </Snackbar>

      {deleting && (
        <StyledLoadingContainer>
          <CircularProgress />
        </StyledLoadingContainer>
      )}
    </>
  );
}
